import React, { ComponentPropsWithRef, ReactElement, ReactNode, forwardRef } from 'react'
import { InputText } from 'primereact/inputtext'
import cn from 'classnames'
import { KeyFilterType } from 'primereact/keyfilter'
import { Message } from 'primereact/message'
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'

import useText from '../../hooks/useText'
import { FieldInfoButton } from '../field-info-button'

interface InputTextProps extends ComponentPropsWithRef<'input'> {
    id: string
    label?: string
    infoText?: string
    error?: string
    subtitle?: ReactNode
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    disabledReason?: string
    keyFilter?: KeyFilterType
    className?: string
    inputClassName?: string
    icon?: string
    endButton?: ReactElement
    'data-testid'?: string
}

export const Input = forwardRef<HTMLInputElement, InputTextProps>(
    (
        {
            label,
            className,
            inputClassName,
            icon,
            keyFilter,
            infoText,
            disabledReason,
            endButton,
            ...props
        }: InputTextProps,
        ref
    ) => {
        const text = useText()
        const inputValue = props.value === null || props.value === undefined ? props.value : String(props.value)

        const inputElement = icon ? (
            <IconField iconPosition="left">
                <InputIcon className={icon} />
                <InputText
                    ref={ref}
                    autoComplete="off"
                    {...props}
                    value={inputValue}
                    invalid={!!props.error}
                    className={cn('w-full', inputClassName)}
                    keyfilter={keyFilter}
                    tooltip={props.disabled && disabledReason ? disabledReason : undefined}
                    tooltipOptions={{
                        position: 'bottom',
                        showOnDisabled: true,
                    }}
                />
            </IconField>
        ) : (
            <InputText
                ref={ref}
                autoComplete="off"
                {...props}
                value={inputValue}
                invalid={!!props.error}
                className={cn('w-full', inputClassName)}
                tooltip={props.disabled && disabledReason ? disabledReason : undefined}
                tooltipOptions={{ position: 'bottom', showOnDisabled: true }}
            />
        )

        const inputWithEndButton = endButton ? (
            <div className="p-inputgroup">
                {inputElement}
                {endButton}
            </div>
        ) : (
            inputElement
        )

        return (
            <div className={className}>
                {label || infoText ? (
                    <div className="flex justify-content-between">
                        {label ? (
                            <label className={cn({ 'required-label': props.required }, 'p-label')} htmlFor={props.id}>
                                {label}
                            </label>
                        ) : null}
                        {infoText ? (
                            <FieldInfoButton
                                ariaLabel={text('lblMoreInformation')}
                                text={infoText}
                                testId={`${props.id}-info-button`}
                            />
                        ) : null}
                    </div>
                ) : null}

                {inputWithEndButton}

                {props.subtitle && (
                    <small
                        id="help-text"
                        className="block"
                        style={{ color: 'var(--text-color-secondary)' }}
                        data-testid={`${props['data-testid'] ?? 'input'}-help-text`}
                    >
                        {props.subtitle}
                    </small>
                )}
                {props.error && (
                    <Message
                        severity="error"
                        text={props.error}
                        data-testid={`${props['data-testid'] ?? 'input'}-error-message`}
                        className="mt-2 flex justify-content-start"
                    />
                )}
            </div>
        )
    }
)
